// ============================================================================
// 📦 VERSION: 2025.11.28.1130
// 📊 Stats Tab - Player Statistics & Achievements
// ============================================================================
import React from 'react';

const StatsTab = ({ playerData }) => {
  
  // Achievement milestones
  const milestones = [
    { traks: 0, name: 'Newcomer', icon: '🌱', bonusTags: 0 },
    { traks: 100, name: 'Bronze Medal', icon: '🥉', bonusTags: 50 },
    { traks: 250, name: 'Silver Medal', icon: '🥈', bonusTags: 100 },
    { traks: 500, name: 'Gold Medal', icon: '🥇', bonusTags: 200 },
    { traks: 1000, name: 'Rising Star', icon: '⭐', bonusTags: 500 },
    { traks: 2500, name: 'Quiz Master', icon: '🎓', bonusTags: 1000 },
    { traks: 5000, name: 'Champion', icon: '🏆', bonusTags: 2000 },
    { traks: 10000, name: 'Legend', icon: '👑', bonusTags: 5000 },
    { traks: 25000, name: 'Grandmaster', icon: '💎', bonusTags: 10000 },
    { traks: 50000, name: 'Elite', icon: '🔥', bonusTags: 25000 },
    { traks: 100000, name: 'Immortal', icon: '⚡', bonusTags: 50000 },
    { traks: 250000, name: 'Transcendent', icon: '🌟', bonusTags: 100000 }
  ];

  const totalTraks = playerData?.stats?.totalTraks || 0;
  
  // Find current achievement
  const getCurrentAchievement = () => {
    for (let i = milestones.length - 1; i >= 0; i--) {
      if (totalTraks >= milestones[i].traks) return milestones[i];
    }
    return milestones[0];
  };

  // Find next achievement
  const getNextAchievement = () => {
    for (let i = 0; i < milestones.length; i++) {
      if (totalTraks < milestones[i].traks) return milestones[i];
    }
    return null;
  };

  const current = getCurrentAchievement();
  const next = getNextAchievement();
  const progress = next ? ((totalTraks - current.traks) / (next.traks - current.traks)) * 100 : 100;

  const stats = playerData?.stats || {};

  return (
    <div className="min-h-screen bg-gray-900 pb-20">
      <div className="max-w-md mx-auto px-4 py-4">
        
        {/* Header */}
        <h1 className="text-white text-2xl font-bold mb-4 flex items-center gap-2">
          <span>📊</span> Your Stats
        </h1>

        {/* Current Achievement Card */}
        <div className="bg-gradient-to-br from-yellow-600 via-orange-500 to-red-500 rounded-2xl p-6 mb-4 text-center">
          <div className="text-6xl mb-2">{current.icon}</div>
          <h2 className="text-white text-2xl font-black">{current.name}</h2>
          <p className="text-yellow-100 text-lg font-bold mt-2">
            🏆 {totalTraks.toLocaleString()} TRAKs
          </p>
          
          {/* Progress to next */}
          {next && (
            <div className="mt-4">
              <div className="flex justify-between text-xs text-yellow-200 mb-1">
                <span>{current.name}</span>
                <span>{next.name}</span>
              </div>
              <div className="h-3 bg-black/30 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-white rounded-full transition-all"
                  style={{ width: `${Math.min(progress, 100)}%` }}
                />
              </div>
              <p className="text-yellow-100 text-xs mt-1">
                {(next.traks - totalTraks).toLocaleString()} TRAKs to {next.name}
              </p>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-gray-800 rounded-xl p-4 text-center border border-purple-500/30">
            <p className="text-purple-400 text-xs mb-1">Total Tags</p>
            <p className="text-white text-2xl font-black">{(stats.totalTags || 0).toLocaleString()}</p>
          </div>
          
          <div className="bg-gray-800 rounded-xl p-4 text-center border border-orange-500/30">
            <p className="text-orange-400 text-xs mb-1">Day Streak</p>
            <p className="text-white text-2xl font-black">🔥 {stats.currentStreak || 0}</p>
          </div>
          
          <div className="bg-gray-800 rounded-xl p-4 text-center border border-green-500/30">
            <p className="text-green-400 text-xs mb-1">Correct Answers</p>
            <p className="text-white text-2xl font-black">{(stats.correctAnswers || 0).toLocaleString()}</p>
          </div>
          
          <div className="bg-gray-800 rounded-xl p-4 text-center border border-blue-500/30">
            <p className="text-blue-400 text-xs mb-1">Questions Answered</p>
            <p className="text-white text-2xl font-black">{(stats.questionsAnswered || 0).toLocaleString()}</p>
          </div>
        </div>

        {/* Accuracy */}
        <div className="bg-gray-800 rounded-xl p-4 mb-6 border border-cyan-500/30">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-cyan-400 text-xs mb-1">Accuracy</p>
              <p className="text-white text-3xl font-black">
                {stats.questionsAnswered > 0 
                  ? Math.round((stats.correctAnswers / stats.questionsAnswered) * 100) 
                  : 0}%
              </p>
            </div>
            <div className="text-5xl">🎯</div>
          </div>
        </div>

        {/* Achievement Progress */}
        <h2 className="text-white font-bold mb-3 flex items-center gap-2">
          <span>🏅</span> Achievements
        </h2>
        
        <div className="space-y-2">
          {milestones.map((milestone, index) => {
            const isUnlocked = totalTraks >= milestone.traks;
            const isCurrent = current.traks === milestone.traks;
            
            return (
              <div 
                key={index}
                className={`flex items-center gap-3 p-3 rounded-xl ${
                  isCurrent 
                    ? 'bg-gradient-to-r from-yellow-600/30 to-orange-600/30 border border-yellow-500'
                    : isUnlocked
                    ? 'bg-gray-800/50 border border-green-500/30'
                    : 'bg-gray-800/30 border border-gray-700 opacity-50'
                }`}
              >
                <span className={`text-3xl ${isUnlocked ? '' : 'grayscale'}`}>
                  {milestone.icon}
                </span>
                <div className="flex-1">
                  <p className={`font-bold ${isUnlocked ? 'text-white' : 'text-gray-500'}`}>
                    {milestone.name}
                  </p>
                  <p className={`text-xs ${isUnlocked ? 'text-gray-400' : 'text-gray-600'}`}>
                    {milestone.traks.toLocaleString()} TRAKs
                  </p>
                </div>
                <div className="text-right">
                  {isUnlocked ? (
                    <span className="text-green-400 text-xl">✓</span>
                  ) : (
                    <span className="text-gray-600 text-sm">🔒</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
};

export default StatsTab;
